import { DELETE, GET, PATCH, POST } from '@/shared/api/requests';
import type {
  CreateSubmittalData,
  PackageSummary,
  SubmittalAttachment,
  SubmittalFilters,
  SubmittalItem,
  SubmittalResponseCode,
  SubmittalStatusSummaryEntry,
  SubmittalSummary,
  UpdateSubmittalData,
  UserMinimal,
} from '../types';

const BASE = '/submittals';

type SubmittalAttachmentUploadUrlResponse = {
  attachment_id: string;
  gcs_object_path: string;
  signed_upload_url: string;
};

const toResults = <T>(data: unknown): T[] => {
  if (Array.isArray(data)) return data as T[];
  if (data && typeof data === 'object' && 'results' in data) {
    return (data as { results?: T[] }).results ?? [];
  }
  return [];
};

export const getSubmittals = async (
  projectId: number,
  filters: SubmittalFilters = {},
): Promise<SubmittalSummary[]> => {
  const params = new URLSearchParams({ project_id: String(projectId) });
  if (filters.status) params.set('status', filters.status);
  if (filters.package_id) params.set('package_id', filters.package_id);
  if (filters.discipline) params.set('discipline', filters.discipline);
  if (filters.reviewer_id) params.set('reviewer_id', String(filters.reviewer_id));
  if (filters.overdue) params.set('overdue', 'true');
  if (filters.current_revision_only === false) params.set('current_revision_only', 'false');
  if (filters.start_date) params.set('start_date', filters.start_date);
  if (filters.end_date) params.set('end_date', filters.end_date);
  if (filters.search) params.set('search', filters.search);
  const response = await GET(`${BASE}/items/?${params.toString()}`);
  return toResults<SubmittalSummary>(response.data);
};

export const getSubmittalById = async (id: string, projectId: number): Promise<SubmittalItem> => {
  const response = await GET(`${BASE}/items/${id}/?project_id=${projectId}&current_revision_only=false`);
  return response.data as SubmittalItem;
};

export const createSubmittal = async (data: CreateSubmittalData): Promise<SubmittalItem> => {
  const response = await POST(`${BASE}/items/`, data);
  return response.data as SubmittalItem;
};

export const updateSubmittal = async (id: string, data: UpdateSubmittalData): Promise<SubmittalItem> => {
  const response = await PATCH(`${BASE}/items/${id}/`, data);
  return response.data as SubmittalItem;
};

export const deleteSubmittal = async (id: string): Promise<void> => {
  await DELETE(`${BASE}/items/${id}/`);
};

export const submitSubmittal = async (id: string): Promise<SubmittalItem> => {
  const response = await POST(`${BASE}/items/${id}/submit/`, {});
  return response.data as SubmittalItem;
};

export const respondToSubmittal = async (
  id: string,
  payload: { reviewer_assignment: string; response_code: SubmittalResponseCode; comment?: string },
): Promise<SubmittalItem> => {
  const response = await POST(`${BASE}/items/${id}/reviewer-response/`, payload);
  return response.data as SubmittalItem;
};

export const closeSubmittal = async (
  id: string,
  payload: {
    final_response_code: SubmittalResponseCode;
    close_comments?: string;
    distribution_recipient_ids?: number[];
  },
): Promise<SubmittalItem> => {
  const response = await POST(`${BASE}/items/${id}/close/`, payload);
  return response.data as SubmittalItem;
};

export const createSubmittalRevision = async (
  id: string,
  payload: { title?: string; description?: string } = {},
): Promise<SubmittalItem> => {
  const response = await POST(`${BASE}/items/${id}/create-revision/`, payload);
  return response.data as SubmittalItem;
};

export const getSubmittalRevisions = async (id: string): Promise<SubmittalSummary[]> => {
  const response = await GET(`${BASE}/items/${id}/revisions/`);
  return toResults<SubmittalSummary>(response.data);
};

export const getSubmittalStatusSummary = async (
  projectId: number,
): Promise<SubmittalStatusSummaryEntry[]> => {
  const response = await GET(`${BASE}/items/status-summary/?project_id=${projectId}`);
  return toResults<SubmittalStatusSummaryEntry>(response.data);
};

export const getSubmittalPackages = async (projectId: number): Promise<PackageSummary[]> => {
  const response = await GET(`${BASE}/packages/?project_id=${projectId}`);
  return toResults<PackageSummary>(response.data);
};

export const createSubmittalPackage = async (payload: {
  project: number;
  title: string;
  description?: string;
  package_number?: string;
  discipline?: string;
}): Promise<PackageSummary> => {
  const response = await POST(`${BASE}/packages/`, payload);
  return response.data as PackageSummary;
};

export const getProjectShareableUsers = async (projectId: number): Promise<UserMinimal[]> => {
  const response = await GET(`/base/projects/${projectId}/shareable-users/`);
  if (Array.isArray(response.data)) return response.data as UserMinimal[];
  if (response.data?.users) return response.data.users as UserMinimal[];
  return toResults<UserMinimal>(response.data);
};

export const uploadSubmittalAttachment = async (
  submittalId: string,
  file: File,
): Promise<SubmittalAttachment> => {
  const uploadUrlResponse = await POST(`${BASE}/attachments/upload-url/`, {
    submittal_id: submittalId,
    file_name: file.name,
    content_type: file.type || 'application/octet-stream',
  });
  const uploadData = uploadUrlResponse.data as SubmittalAttachmentUploadUrlResponse;
  await uploadFileToSignedUrl(file, uploadData.signed_upload_url);
  const finalizeResponse = await POST(`${BASE}/attachments/finalize/`, {
    submittal_id: submittalId,
    attachment_id: uploadData.attachment_id,
    gcs_object_path: uploadData.gcs_object_path,
    original_file_name: file.name,
    mime_type: file.type || 'application/octet-stream',
    file_size: file.size,
  });
  return finalizeResponse.data as SubmittalAttachment;
};

export const deleteSubmittalAttachment = async (id: string): Promise<void> => {
  await DELETE(`${BASE}/attachments/${id}/`);
};

export const getSubmittalsExportCsvUrl = (projectId: number, filters: SubmittalFilters = {}): string => {
  const params = new URLSearchParams({ project_id: String(projectId) });
  if (filters.status) params.set('status', filters.status);
  if (filters.package_id) params.set('package_id', filters.package_id);
  if (filters.overdue) params.set('overdue', 'true');
  return `/api/v1${BASE}/items/export-csv/?${params.toString()}`;
};

const uploadFileToSignedUrl = async (file: File, signedUrl: string): Promise<void> => {
  await new Promise<void>((resolve, reject) => {
    const xhr = new XMLHttpRequest();
    xhr.addEventListener('load', () => {
      if (xhr.status >= 200 && xhr.status < 300) {
        resolve();
        return;
      }
      reject(new Error(`Attachment upload failed with status ${xhr.status}.`));
    });
    xhr.addEventListener('error', () => reject(new Error('Attachment upload failed.')));
    xhr.open('PUT', signedUrl);
    xhr.setRequestHeader('Content-Type', file.type || 'application/octet-stream');
    xhr.send(file);
  });
};
